/* What a client may choose on a quote (UX-100): optional phases and lines they tick, and option
   groups (phases across the quote, lines within their phase) of which they pick exactly one.
   The picks are the ids of the optional and grouped items that are in, as /price and /accept
   take them. */
import type { Quote } from "../api/types";

type Phase = Quote["phases"][number];
type Line = Phase["lines"][number];

export const choosable = (item: Phase | Line) => item.optional || item.option_group !== null;

/** The picks as the quote was sent: each line's own pick, even in a phase that's out. */
export function initialChoices(q: Quote): Set<string> {
  const ids = new Set<string>();
  for (const ph of q.phases) {
    if (choosable(ph) && ph.included) ids.add(ph.id);
    for (const l of ph.lines) if (choosable(l) && l.selected) ids.add(l.id);
  }
  return ids;
}

/** `chosen` with `id` picked from its group: the group's other members are dropped. */
export function pickOne(chosen: ReadonlySet<string>, members: { id: string }[], id: string): Set<string> {
  const next = new Set(chosen);
  for (const m of members) next.delete(m.id);
  next.add(id);
  return next;
}

/** `chosen` with `id` ticked or unticked. */
export function toggle(chosen: ReadonlySet<string>, id: string, on: boolean): Set<string> {
  const next = new Set(chosen);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}

/** The members of each option group, in quote order, keyed by group name. */
export function groupsOf<T extends Phase | Line>(items: T[]): Map<string, T[]> {
  const found = new Map<string, T[]>();
  for (const item of items)
    if (item.option_group) found.set(item.option_group, [...(found.get(item.option_group) ?? []), item]);
  return found;
}

/** What the client has picked, in words ("Package: Premium", "Training"): the optional and
    grouped items that are in, leaving out lines in a phase they didn't pick. */
export function chosenNames(q: Quote, chosen: ReadonlySet<string>): string[] {
  const names: string[] = [];
  const named = (item: Phase | Line, name: string) =>
    names.push(item.option_group ? `${item.option_group}: ${name}` : name);
  for (const ph of q.phases) {
    if (choosable(ph) && !chosen.has(ph.id)) continue;
    if (choosable(ph)) named(ph, ph.name);
    for (const l of ph.lines) if (choosable(l) && chosen.has(l.id)) named(l, l.description);
  }
  return names;
}
